import {ChangeEvent, FC, useContext, useEffect, useRef, useState} from 'react';
import {useRecoilValue, useRecoilValueLoadable, useSetRecoilState} from 'recoil';
import {useForm, SubmitHandler, Controller} from 'react-hook-form';
import ReactSelect from 'react-select';
import {YMaps} from '@pbe/react-yandex-maps';
import {toast} from 'react-toastify';

import {Autocomplete, Avatar, Box, Button, IconButton, Stack, TextField, Tooltip, Typography} from '@mui/material';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import CircularProgress from '@mui/material/CircularProgress';
import PhoneInput from '../../../ui/phone-input/PhoneInput';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import IsActiveStatus from '../../../ui/is-active-status/IsActiveStatus';
import {ModalContainer} from '../../modals/ModalContainer';
import {ConfirmAction} from '../../modals/ConfirmAction';
import AddIcon from '@mui/icons-material/Add';
import BackspaceIcon from '@mui/icons-material/Backspace';
import EditIcon from '@mui/icons-material/Edit';
import {ChangePassword} from './ChangePassword';
import {HomeAddressMap} from './HomeAddressMap';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {EventBusContext} from '../../../providers/EventBus';

import {useSearchCompanies} from '../../../hooks/useSearchCompanies';
import {useFileUpload} from '../../../hooks/useFileUpload';

import {mapWithRoleOptions, roles} from '../../../shared/roles.data';

import {IUserAccount, TypeAccountRoles} from '../../../shared/types/user-account.types';

import {UserAccountService} from '../../../api/services/user-account.service';

import {emailRegExp} from '../../../shared/validation/emailRegExp';

import {visuallyHidden} from '@mui/utils';

import {userAccountsQueryTrigger} from '../../../store/users/atoms';
import {companyByIdQuery} from '../../../store/company/selectors';
import {userAccountPhotoQueryFamily} from '../../../store/users/selectors';

import {yellow} from '@mui/material/colors';

import picture from '../../../assets/Subtract.jpg';

import {copyToClipboard} from '../../../shared/helpers/copyToClipBoard';
import {checkAuthorities} from '../../../shared/helpers/checkAuthorities';
import {authState} from '../../../store/auth/atoms';

interface IEditUserAccount {
  handleClose: () => void;
  loading: boolean;
  setLoading: (arg: boolean) => void;
  account: IUserAccount;
}

const iconButtonStyle = {
  padding: '5px !important',
};

const requiredStyle = {
  color: '#EB3333',
};

const labelStyle = {
  color: '#00203399',
};

export const EditUserAccount: FC<IEditUserAccount> = ({handleClose, loading, setLoading, account}) => {
  const {contents: company, state} = useRecoilValueLoadable(companyByIdQuery(account?.companyId));
  const {contents: photoSrc} = useRecoilValueLoadable(userAccountPhotoQueryFamily(account?.photo ? account.id : 0));
  const {
    control,
    handleSubmit,
    formState: {isDirty},
    watch,
    setValue,
    getValues,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      userLogin: account.userLogin,
      email: account.email,
      lastName: account.lastName,
      firstName: account.firstName,
      patronymic: account.patronymic || null,
      position: account.position || null,
      roles: account.roles?.filter((i) => i !== TypeAccountRoles.VIEWER)?.map((i) => mapWithRoleOptions.get(i)) || [],
      phoneNumber: account.phoneNumber,
      comments: account.comments || null,
      companyId: account.companyId || null,
      company: state === 'hasValue' && company ? company : null,
      homeAddress: account.homeAddress || null,
      geoPos: account.homeAddress ? [account.homeAddressLatitude, account.homeAddressLongitude] : undefined,
    },
  });
  const {files, fileNames, setFiles} = useFileUpload();
  const {handleSearch, data, searchTerm, searchLoading} = useSearchCompanies();
  const triggerUsersQuery = useSetRecoilState(userAccountsQueryTrigger);
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openBlock, setOpenBlock] = useState(false);
  const [openUnblock, setOpenUnblock] = useState(false);
  const [openChangePass, setOpenChangePass] = useState(false);
  const [openMap, setOpenMap] = useState(false);
  const {bus} = useContext(EventBusContext);
  const {user} = useRecoilValue(authState);

  const inputRef = useRef<HTMLInputElement>(null);

  const geoPosWatch = watch('geoPos');
  const homeAddressWatch = watch('homeAddress');
  const companyId = watch('companyId');

  const toggleOpenMap = () => {
    setOpenMap(!openMap);
  };

  const clearHomeAddress = () => {
    setValue('geoPos', undefined);
    setValue('homeAddress', '');
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFiles(e);
  };

  const copyValue = copyToClipboard(getValues);

  const toggleModalToDelete = () => {
    setOpenDelete(!openDelete);
  };

  const toggleModalToBlock = () => {
    setOpenBlock(!openBlock);
  };

  const toggleModalToUnblock = () => {
    setOpenUnblock(!openUnblock);
  };

  const toggleModalToChangePass = () => {
    setOpenChangePass(!openChangePass);
  };

  const onSubmit: SubmitHandler<any> = async (data, e) => {
    const roles = data.roles?.map((v: {label: string; value: string}) => v.value);
    const formData = new FormData();

    try {
      setLoading(true);

      await UserAccountService.update({
        ...account,
        ...data,
        lastName: data.lastName.trim(),
        firstName: data.firstName.trim(),
        patronymic: data.patronymic ? data.patronymic.trim() : null,
        position: data.position ? data.position.trim() : null,
        comments: data.comments ? data.comments.trim() : null,
        roles: roles.length ? [TypeAccountRoles.VIEWER, ...roles] : [TypeAccountRoles.VIEWER],
        companyId: data.company?.id || null,
        homeAddress: data.homeAddress || null,
        homeAddressLatitude: data.geoPos ? data.geoPos[0] : null,
        homeAddressLongitude: data.geoPos ? data.geoPos[1] : null,
      });

      fileNames[0] && formData.append('photo', files[0]);

      fileNames[0] && (await UserAccountService.addUserPhoto(account.id, formData));

      toast.success('Данные изменены', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      triggerUsersQuery((v) => v + 1);

      setTimeout(() => {
        setLoading(false);
        handleClose();
      });
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const handleDeleteAccount = async (setLoadingModal: (arg: boolean) => void) => {
    try {
      setLoadingModal(true);
      setLoading(true);

      await UserAccountService.delete(account.id);

      triggerUsersQuery((v) => v + 1);

      setTimeout(() => {
        setLoadingModal(false);
        setLoading(false);
        toggleModalToDelete();
        handleClose();
      });
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoadingModal(false);
      setLoading(false);
    }
  };

  const handleBlockAccount = async (setLoadingModal: (arg: boolean) => void) => {
    try {
      setLoadingModal(true);
      setLoading(true);

      await UserAccountService.block(account.uid, true);

      triggerUsersQuery((v) => v + 1);

      setTimeout(() => {
        setLoadingModal(false);
        setLoading(false);
        toggleModalToBlock();
      });
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoadingModal(false);
      setLoading(false);
    }
  };

  const handleUnblockAccount = async (setLoadingModal: (arg: boolean) => void) => {
    try {
      setLoadingModal(true);
      setLoading(true);

      await UserAccountService.block(account.uid, false);

      triggerUsersQuery((v) => v + 1);

      setTimeout(() => {
        setLoadingModal(false);
        setLoading(false);
        toggleModalToBlock();
      });
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoadingModal(false);
      setLoading(false);
    }
  };

  const isMe = account.uid === user?.uid;
  const isAdmin = checkAuthorities(
    [TypeAccountRoles.ADMIN, TypeAccountRoles.FUNCTIONAL_ADMIN],
    user?.roles as TypeAccountRoles[],
  );
  const isPossibleToEdit = account.active && (isMe || isAdmin);
  const isSubmitDisabled = !isPossibleToEdit || !account.active || !companyId || (!isDirty && !fileNames[0]);

  useEffect(() => {
    if (state === 'loading') {
      setLoading(true);
    }
    if (state === 'hasValue' && company) {
      setLoading(false);

      if (company) {
        setValue('companyId', company.id, {shouldDirty: true, shouldValidate: true});
        setValue('company', company, {shouldDirty: true, shouldValidate: true});
      }
    }
    if (state === 'hasError') {
      bus?.emit('error', [company]);
    }
  }, [state, company]);

  return (
    <>
      <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={1.5}>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Typography variant="h5">Редактировать пользователя</Typography>
            <IsActiveStatus isActive={account.active} />
          </Stack>
          <Stack direction="column" spacing={1} sx={{overflow: 'hidden'}}>
            <Stack direction="row" spacing={2}>
              <Typography sx={labelStyle} variant="subtitle2">
                Фото профиля
              </Typography>
              <button
                type="button"
                style={{cursor: 'pointer'}}
                onClick={() => inputRef?.current?.click()}
                disabled={!isPossibleToEdit}
              >
                {fileNames.length ? 'Выбрать другой' : 'Выбрать файл'}
              </button>
              <Tooltip title="Максимальный размер файла: 512KB">
                <ErrorOutlineIcon htmlColor={yellow[900]} />
              </Tooltip>
            </Stack>
            <Stack
              direction="column"
              spacing={2}
              sx={{
                height: '116px',
                border: '1px solid #D4D4D4',
                justifyContent: 'center',
                alignItems: fileNames[0] ? 'normal' : 'center',
                p: '0 16px',
              }}
            >
              <Stack direction="row" sx={{justifyContent: 'center'}}>
                <Avatar
                  sx={{width: 70, height: 70}}
                  variant="rounded"
                  src={fileNames[0] ? URL.createObjectURL(files[0]) : photoSrc ? photoSrc : picture}
                  alt="Avatar"
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography textOverflow="ellipsis" variant="subtitle2" sx={{color: '#00203399'}} noWrap>
                  {fileNames[0] ? fileNames[0] : ''}
                </Typography>
                <input
                  type="file"
                  ref={inputRef}
                  style={{display: 'none'}}
                  onChange={onChange}
                  accept="image/*"
                  disabled={!account.active}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Логин
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="userLogin"
                disabled
                size="small"
                rules={{
                  required: 'Обязательно для заполнения',
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Допустимы латинские буквы и цифры, нижнее подчеркивание',
                  },
                }}
              />
            </Stack>
            <Stack direction="column" sx={{width: '48.5%', justifyContent: 'center', alignItems: 'center'}} spacing={1}>
              <Button
                disabled={!isPossibleToEdit}
                onClick={toggleModalToChangePass}
                size="small"
                sx={{width: '50%'}}
                variant="outlined"
              >
                Сменить пароль
              </Button>
            </Stack>
          </Stack>
          <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
            <Stack direction="row" spacing={1}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  ФИО
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <IconButton
                onClick={() => copyValue(['lastName', 'firstName', 'patronymic'])}
                sx={{padding: '0 !important'}}
              >
                <ContentCopyIcon sx={{height: '17px !important', width: '17px !important'}} />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <ControlledInput
                control={control}
                name="lastName"
                size="small"
                disabled={loading || !isPossibleToEdit}
                sx={{borderRadius: '0 !important'}}
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
              <ControlledInput
                control={control}
                name="firstName"
                size="small"
                disabled={loading || !isPossibleToEdit}
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
              <ControlledInput
                control={control}
                name="patronymic"
                size="small"
                disabled={loading || !isPossibleToEdit}
              />
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Email
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="email"
                size="small"
                disabled={loading || !isPossibleToEdit}
                rules={{
                  pattern: {
                    value: emailRegExp,
                    message: 'Некорректный email',
                  },
                  required: 'Обязательно для заполнения',
                }}
                InputProps={{
                  endAdornment: (
                    <Stack direction="row">
                      <IconButton onClick={() => copyValue(['email'])} size="small" sx={iconButtonStyle}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton disabled={loading || !isPossibleToEdit} size="small" sx={iconButtonStyle}>
                        <EmailIcon fontSize="inherit" htmlColor="#0D99FF" />
                      </IconButton>
                    </Stack>
                  ),
                }}
              />
            </Stack>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Номер телефона
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <PhoneInput
                control={control}
                name="phoneNumber"
                disabled={loading || !isPossibleToEdit}
                rules={{
                  required: 'Обязательно для заполнения',
                  pattern: {
                    value: /^(\+7 \([1-9]\d{2}\) \d{3}-\d{2}-\d{2}( #\d+)?$)/,
                    message: 'Некорректный номер или добавочный',
                  },
                }}
                size="small"
                endAdornment={
                  <Stack direction="row" spacing={0.25}>
                    <IconButton onClick={() => copyValue(['phoneNumber'])} size="small" sx={iconButtonStyle}>
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton disabled={loading || !isPossibleToEdit} size="small" sx={iconButtonStyle}>
                      <LocalPhoneIcon fontSize="inherit" htmlColor="#2CC378" />
                    </IconButton>
                  </Stack>
                }
              />
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Typography sx={labelStyle} variant="subtitle2">
                Должность
              </Typography>
              <ControlledInput control={control} name="position" disabled={loading} size="small" />
            </Stack>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Typography sx={labelStyle} variant="subtitle2">
                Домашний адрес
              </Typography>
              {homeAddressWatch ? (
                <Tooltip title={homeAddressWatch}>
                  <Box>
                    <ControlledInput
                      control={control}
                      name="homeAddress"
                      disabled
                      sx={{
                        '& input:disabled': {
                          '-webkit-text-fill-color': `rgb(0 0 0 / 85%) !important`,
                        },
                      }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <>
                            {geoPosWatch ? (
                              <>
                                <Tooltip title="Удалить">
                                  <IconButton size="small" onClick={clearHomeAddress} sx={iconButtonStyle}>
                                    <BackspaceIcon color="error" fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Изменить">
                                  <IconButton size="small" onClick={toggleOpenMap} sx={iconButtonStyle}>
                                    <EditIcon color="primary" fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title="Открыть карту">
                                <IconButton onClick={toggleOpenMap} sx={iconButtonStyle}>
                                  <AddIcon htmlColor="#2CC378" fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        ),
                      }}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <ControlledInput
                  control={control}
                  name="homeAddress"
                  disabled
                  sx={{
                    '& input:disabled': {
                      '-webkit-text-fill-color': `rgb(0 0 0 / 85%) !important`,
                    },
                  }}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <>
                        {geoPosWatch ? (
                          <>
                            <Tooltip title="Удалить">
                              <IconButton size="small" onClick={clearHomeAddress} sx={iconButtonStyle}>
                                <BackspaceIcon color="error" fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Изменить">
                              <IconButton size="small" onClick={toggleOpenMap} sx={iconButtonStyle}>
                                <EditIcon color="primary" fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Открыть карту">
                            <IconButton onClick={toggleOpenMap} sx={iconButtonStyle}>
                              <AddIcon htmlColor="#2CC378" fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    ),
                  }}
                />
              )}
              <ControlledInput control={control} name="geoPos" sx={visuallyHidden} />
            </Stack>
          </Stack>
          <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
            <Typography sx={labelStyle} variant="subtitle2">
              Роли
            </Typography>
            <Controller
              name="roles"
              control={control}
              render={({field: {onChange, value, ref}}) => (
                <ReactSelect
                  options={roles}
                  value={value}
                  isDisabled={loading || !isPossibleToEdit}
                  onChange={onChange}
                  ref={ref}
                  isMulti
                  placeholder=" "
                />
              )}
            />
          </Stack>
          <Stack direction="column" sx={{width: '100%'}}>
            <Typography sx={labelStyle} variant="subtitle2">
              Название компании
            </Typography>
            <Controller
              control={control}
              name="company"
              rules={{
                required: 'Обязательно для заполнения',
              }}
              render={({field: {value}, fieldState: {error}}) => (
                <Autocomplete
                  open={open}
                  disabled={loading || !isPossibleToEdit}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  onChange={(_, newValue) => {
                    setValue('company', newValue || null, {shouldDirty: true, shouldValidate: true});
                    setValue('companyId', newValue?.id || null, {shouldDirty: true, shouldValidate: true});

                    if (newValue?.id) {
                      clearErrors('companyId');
                    }
                  }}
                  value={value}
                  inputValue={searchTerm}
                  getOptionLabel={(option) => `${option.fullCompanyName},  ИНН: ${option.inn}`}
                  onInputChange={(_, newInputValue) => {
                    handleSearch(newInputValue);
                  }}
                  options={data}
                  loading={searchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Название / ИНН / комментарий"
                      size="small"
                      error={!!error}
                      helperText={error ? error.message : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Stack>
          <Stack direction="column" spacing={0.25}>
            <Typography sx={labelStyle} variant="subtitle2">
              Комментарий
            </Typography>
            <ControlledInput
              control={control}
              name="comments"
              disabled={loading || !isPossibleToEdit}
              size="small"
              rows={4}
              multiline
            />
          </Stack>
        </Stack>
        <Stack direction="row" sx={{justifyContent: 'space-between', mt: 4}}>
          <LoadingButton
            onClick={() => {
              (ref?.current as any).requestSubmit();
            }}
            variant="outlined"
            color="success"
            disabled={isSubmitDisabled}
            loading={loading}
          >
            Сохранить изменения
          </LoadingButton>
          <Stack direction="row" spacing={1.5}>
            {isAdmin &&
              !isMe &&
              (account.active ? (
                <LoadingButton loading={loading} onClick={toggleModalToBlock} variant="outlined">
                  Заблокировать
                </LoadingButton>
              ) : (
                <LoadingButton loading={loading} onClick={toggleModalToUnblock} variant="outlined">
                  Разблокировать
                </LoadingButton>
              ))}
          </Stack>
        </Stack>
      </form>
      {openDelete && (
        <ModalContainer
          sideEffect={handleDeleteAccount}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={openDelete}
              handleClose={toggleModalToDelete}
              handleSideEffect={sideEffect}
              title="Подтвердите удаление"
              buttonTitle="Удалить"
            />
          )}
        />
      )}
      {openBlock && (
        <ModalContainer
          sideEffect={handleBlockAccount}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={openBlock}
              handleClose={toggleModalToBlock}
              handleSideEffect={sideEffect}
              title="Подтвердите блокировку"
              buttonTitle="Заблокировать"
            />
          )}
        />
      )}
      {openUnblock && (
        <ModalContainer
          sideEffect={handleUnblockAccount}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={openUnblock}
              handleClose={toggleModalToUnblock}
              handleSideEffect={sideEffect}
              title="Вы действительно желаете разблокировать?"
              buttonTitle="Разблокировать"
            />
          )}
        />
      )}
      {openChangePass && (
        <ModalContainer
          renderModal={() => <ChangePassword open={openChangePass} handleClose={toggleModalToChangePass} />}
        />
      )}
      {openMap && (
        <YMaps
          query={{
            apikey: process.env.REACT_APP_YANDEX_MAPS_API_KEY,
            lang: 'ru_RU',
          }}
        >
          <HomeAddressMap
            open={openMap}
            handleClose={toggleOpenMap}
            setValue={setValue}
            defaultGeoPos={geoPosWatch as number[] | undefined}
            defaultHomeAddress={homeAddressWatch as string | undefined}
          />
        </YMaps>
      )}
    </>
  );
};
